/**
 * RTA (real-time analyzer) bar chart — mobile's light-themed Canvas2D
 * counterpart to web's `web/src/components/room/charts/rtaDraw.ts`. Same
 * geometry (`computeRtaGeometry`), different ink (see `./_shared.ts`'s
 * module doc for why the two paint layers are intentionally separate).
 */

import type { ChartPad } from '../layout';
import { computeRtaGeometry, type OctaveFraction } from '../rta';
import { setupCanvas, drawEmptyMessage, drawLogFreqAxisGrid, drawBars, INK } from './_shared';

export const RTA_PAD: ChartPad = { t: 14, r: 14, b: 26, l: 40 };

export interface DrawRtaOpts {
  freqs: number[];
  mag: number[];
  width: number;
  height: number;
  fMin?: number;
  fMax?: number;
  bandsPerOctave?: OctaveFraction;
}

export function drawRtaCanvas(canvas: HTMLCanvasElement, opts: DrawRtaOpts): void {
  const s = setupCanvas(canvas, opts.width, opts.height);
  if (!s) return;
  const { ctx, W, H } = s;

  const geo = computeRtaGeometry({
    freqs: opts.freqs,
    mag: opts.mag,
    width: opts.width,
    height: opts.height,
    pad: RTA_PAD,
    fMin: opts.fMin,
    fMax: opts.fMax,
    bandsPerOctave: opts.bandsPerOctave,
    measureWidth: (label) => ctx.measureText(label).width,
  });

  if (geo.empty) {
    drawEmptyMessage(ctx, W, H, 'No frequency response data yet');
    return;
  }

  drawLogFreqAxisGrid(ctx, geo.frame, geo.xTicks, geo.yTicks);

  const bars = geo.bars
    .filter((bar) => bar.db != null)
    .map((bar) => ({ x0: bar.x0, x1: bar.x1, y: bar.y, color: INK.neutralBar }));
  drawBars(ctx, geo.frame, bars);
}
